import { useEffect, type RefObject } from 'react';

/** Les éléments qui prennent le focus au clavier, dans l'ordre du document. */
const SELECTEUR_FOCUS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Retient le focus DANS une boîte tant qu'elle est ouverte.
 *
 * Sans cela, `Tab` sort du modal et promène l'utilisateur derrière la page : au
 * clavier, on ne sait plus où l'on est ni ce qu'on valide. Trois gestes, et un
 * quatrième qui compte autant :
 *   1. `Tab` sur le dernier élément revient au premier ;
 *   2. `Maj+Tab` sur le premier va au dernier ;
 *   3. à l'ouverture, le focus entre dans la boîte (premier élément focusable) ;
 *   4. à la fermeture, il REVIENT là où il était — sinon on repart du début du
 *      document, ce qui est une autre façon de perdre l'utilisateur.
 *
 * Le piège ne crée aucun élément : il réutilise ce que l'écran contient déjà.
 */
export function usePiegeFocus(conteneur: RefObject<HTMLElement | null>, actif: boolean): void {
  useEffect(() => {
    if (!actif) return;
    const boite = conteneur.current;
    if (!boite) return;

    // 4. Mémorisé AVANT de déplacer le focus, restauré au démontage.
    const precedent = document.activeElement as HTMLElement | null;

    // Visibilité : on se fie à l'attribut `hidden`, pas à `offsetParent` (que jsdom
    // ne calcule pas, et qui rendrait la liste vide dans les tests — donc le piège
    // silencieusement inopérant).
    const focusables = () =>
      Array.from(boite.querySelectorAll<HTMLElement>(SELECTEUR_FOCUS)).filter(
        (element) => element.closest('[hidden]') === null,
      );

    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key !== 'Tab') return;
      const elements = focusables();
      if (elements.length === 0) return;
      const premier = elements[0]!;
      const dernier = elements[elements.length - 1]!;
      const actuel = document.activeElement;

      if (evenement.shiftKey && (actuel === premier || !boite.contains(actuel))) {
        evenement.preventDefault();
        dernier.focus();
      } else if (!evenement.shiftKey && (actuel === dernier || !boite.contains(actuel))) {
        evenement.preventDefault();
        premier.focus();
      }
    };

    document.addEventListener('keydown', surTouche, true);
    return () => {
      document.removeEventListener('keydown', surTouche, true);
      precedent?.focus?.();
    };
  }, [conteneur, actif]);
}
